// ---------------------------------------------------------------------------
// Depth-based silhouette outline (plan 23) — the ink-line half of the custom
// toon look. One fullscreen pass over the composer's scene buffer.
//
// Depth-ONLY edge detection, deliberately no normals: the game's geometry is
// flat-shaded faceted, so a normal-discontinuity detector would ink every
// facet edge and every seam between the prims that make up one body. Depth
// gives clean outer silhouettes and skips coplanar/touching prims — the "no
// internal seams" requirement. The edge test is the SECOND derivative of
// linearized view depth, clamped to neighbors-farther-only: smooth grazing
// slopes (terrain) have ≈0 second derivative so they don't ink, and the line
// lands on the NEAR side of a step only (single line, not double).
//
// Buffer contract (three r185, verified): RenderPass renders into the
// composer's CURRENT readBuffer and the buffer roles alternate frames (odd
// swap count per frame), so tDiffuse/tDepth are bound from `readBuffer` inside
// render() EVERY frame — never cached at construction. Both composer RTs get a
// DepthTexture (render.ts).
//
// The outline tint tracks the live sky/fog color (multiply-darkened): fog
// swings powder-blue → near-black navy → dusk orange → storm slate, and a
// fixed black line would vanish at night. render.ts copies scene.fog.color +
// fog.far into the uniforms each frame; lines also fade out by fog-far so
// they dissolve into the fog instead of popping at the far plane.
//
// Client-only (imports three/examples) — never on the server path.
// ---------------------------------------------------------------------------
import * as THREE from "three";
import { Pass, FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";

// Shared tunables (the RIM pattern): the F6 dev panel mutates these live; bake
// chosen values into the defaults below. Client-visual only. OUTLINE_DEFAULTS
// is the single source of truth (mirrors toon.ts RIM_DEFAULTS / RAMP_DEFAULTS)
// so the panel's "reset all" can't drift from the baked-in values.
export const OUTLINE_DEFAULTS = { thickness: 1.0, threshold: 0.02, strength: 0.85, darken: 0.45 };
export const OUTLINE = {
  thickness: { value: OUTLINE_DEFAULTS.thickness }, // sample offset, effective pixels
  threshold: { value: OUTLINE_DEFAULTS.threshold }, // relative depth-step threshold (× view depth)
  strength: { value: OUTLINE_DEFAULTS.strength }, // line opacity
  darken: { value: OUTLINE_DEFAULTS.darken }, // line color = sky × darken
};

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}`;

const FRAG = /* glsl */ `
#include <packing>
uniform sampler2D tDiffuse;
uniform sampler2D tDepth;
uniform vec2 uTexel;
uniform float uNear;
uniform float uFar;
uniform float uIsOrtho;
uniform vec3 uSky;
uniform float uFogFar;
uniform float uThickness;
uniform float uThreshold;
uniform float uStrength;
uniform float uDarken;
varying vec2 vUv;

// Linearized view depth (positive, world units toward the far plane).
float linD( vec2 uv ) {
  float d = texture2D( tDepth, uv ).x;
  return uIsOrtho > 0.5
    ? -orthographicDepthToViewZ( d, uNear, uFar )
    : -perspectiveDepthToViewZ( d, uNear, uFar );
}

void main() {
  vec4 col = texture2D( tDiffuse, vUv );
  vec2 t = uTexel * uThickness;
  float c = linD( vUv );
  // Second derivative along each axis, clamped to "neighbors farther" so the
  // line draws once, on the near side of the step; flat slopes stay clean.
  float ex = linD( vUv + vec2( t.x, 0.0 ) ) + linD( vUv - vec2( t.x, 0.0 ) ) - 2.0 * c;
  float ey = linD( vUv + vec2( 0.0, t.y ) ) + linD( vUv - vec2( 0.0, t.y ) ) - 2.0 * c;
  float edge = max( ex, 0.0 ) + max( ey, 0.0 );
  // Threshold scales with view depth (a 0.1u step matters up close, not far
  // off), and lines dissolve into the fog well before the far plane.
  float m = smoothstep( uThreshold * c, uThreshold * c * 2.0, edge ) * uStrength;
  m *= 1.0 - smoothstep( uFogFar * 0.6, uFogFar, c );
  gl_FragColor = vec4( mix( col.rgb, uSky * uDarken, m ), col.a );
}`;

export class ToonOutlinePass extends Pass {
  /** The active render camera — render.ts re-points this on setCamera (the
   *  studio world editor swaps in an ORTHO top-down cam with far 3000, so
   *  near/far/isOrtho are read per frame, never baked from config). */
  camera: THREE.Camera;
  readonly uniforms: Record<string, THREE.IUniform>;
  private material: THREE.ShaderMaterial;
  private fsQuad: FullScreenQuad;

  constructor(camera: THREE.Camera) {
    super();
    this.camera = camera;
    this.uniforms = {
      tDiffuse: { value: null },
      tDepth: { value: null },
      uTexel: { value: new THREE.Vector2(1 / 1024, 1 / 1024) },
      uNear: { value: 0.1 },
      uFar: { value: 170 },
      uIsOrtho: { value: 0 },
      uSky: { value: new THREE.Color(0x9fd3ff) },
      uFogFar: { value: 135 },
      // Shared tunable objects — the F6 panel drives every value live.
      uThickness: OUTLINE.thickness,
      uThreshold: OUTLINE.threshold,
      uStrength: OUTLINE.strength,
      uDarken: OUTLINE.darken,
    };
    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false, // hygiene: don't scribble the writeBuffer's depth texture
    });
    this.fsQuad = new FullScreenQuad(this.material);
  }

  /** Composer feeds EFFECTIVE pixel sizes (w×pixelRatio) — texel = 1/effective. */
  override setSize(width: number, height: number): void {
    (this.uniforms.uTexel!.value as THREE.Vector2).set(1 / width, 1 / height);
  }

  override render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    // Bind from readBuffer PER FRAME — the composer's RT roles alternate.
    this.uniforms.tDiffuse!.value = readBuffer.texture;
    this.uniforms.tDepth!.value = readBuffer.depthTexture;
    const cam = this.camera as THREE.PerspectiveCamera;
    this.uniforms.uNear!.value = cam.near;
    this.uniforms.uFar!.value = cam.far;
    this.uniforms.uIsOrtho!.value = (this.camera as THREE.OrthographicCamera).isOrthographicCamera ? 1 : 0;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.fsQuad.dispose();
  }
}
